import { create } from 'zustand';
import type { FavoriteEntity } from '@shared/flow/signal-chips';
import { useImpersonateWriteModeStore } from '../../../lib/impersonate-write-mode';

/**
 * UI state for the favorites rows. Nothing persists in the browser: edit mode and View as are session-only, and
 * the favorites size is a server-side per-user preference (`signalsHeadlineChipsSize`).
 *
 * `editEntity` is the ONE tab whose favorites row is in edit mode (null = none), so editing Companies never puts
 * Signals in edit mode. `viewAsSfUserId` is Signals-only and is the single source the Signals favorites, counts,
 * and list queries all read.
 */
export interface FavoritesUiState {
  editEntity: FavoriteEntity | null;
  viewAsSfUserId: string | null;
}

export const initialState: FavoritesUiState = {
  editEntity: null,
  viewAsSfUserId: null,
};

export const useFavoritesUiStore = create<FavoritesUiState>(() => ({ ...initialState }));

// `collapsed` used to persist here under this key; the size is server-side now, so clear the orphan.
try {
  if (typeof window !== 'undefined') window.localStorage.removeItem('flow-signal-chips-ui');
} catch {
  // Storage can be unavailable (privacy mode); there is nothing to clean up then.
}

/**
 * Puts one tab's favorites in edit mode. View as is Signals-only and is picked from the Signals edit bar, so it
 * ends when edit mode turns off (`null`) OR moves to another tab; otherwise it would stay silently active on
 * Signals with no edit bar or indicator to explain it.
 */
export function setFavoritesEditEntity(entity: FavoriteEntity | null): void {
  useFavoritesUiStore.setState(entity === 'signal' ? { editEntity: entity } : { editEntity: entity, viewAsSfUserId: null });
}

export function setFavoritesViewAs(sfUserId: string | null): void {
  useFavoritesUiStore.setState({ viewAsSfUserId: sfUserId });
}

/** True only while THIS tab's favorites are being edited. */
export function useFavoritesEditMode(entity: FavoriteEntity): boolean {
  return useFavoritesUiStore(s => s.editEntity === entity);
}

export function useFavoritesViewAs(): string | null {
  return useFavoritesUiStore(s => s.viewAsSfUserId);
}

/**
 * The one effective viewer for the Signals favorites row, admin bar, counts and table model. Impersonation and
 * View as are mutually exclusive lenses and impersonation wins. The stored id is ignored (not cleared) while
 * impersonating.
 */
export function useEffectiveFavoritesViewAs(): string | null {
  const viewAs = useFavoritesUiStore(s => s.viewAsSfUserId);
  const impersonating = useImpersonateWriteModeStore(s => s.impersonating === true);
  return impersonating ? null : viewAs;
}
